import { useNavigate } from 'react-router-dom';
import { Icon } from '@/deploy/components/ui/Icon';
import { EnvBadge } from '@/deploy/components/ui/EnvBadge';
import { Badge } from '@/deploy/components/ui/Badge';
import { useSessionStore } from '@/deploy/stores/session.store';
import { MOCK_BETA_SLOTS, MOCK_CONTAINERS, MOCK_DEV_ENVS, MOCK_INSTANCES } from '@/deploy/data/mock';
import styles from './PipelineTimeline.module.scss';

// Nouveau flow :
//   Dev (plusieurs envs perso)        ╮
//   Beta (5 slots A..E)               │  PAS de lien direct vers Pré-prod
//                                     ╯
//   ——— Pré-prod (from scratch) ──→ Prod (1 clic) ———

export function PipelineTimeline() {
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());

  const devEnvs   = MOCK_DEV_ENVS.filter((e) => e.projectId === project.id);
  const betaSlots = MOCK_BETA_SLOTS.filter((s) => s.projectId === project.id);
  const betaOccupied = betaSlots.filter((s) => s.allocated).length;

  const instances = MOCK_INSTANCES.filter((i) => i.projectId === project.id);
  const prodInstances   = instances.filter((i) => i.environment === 'prod');
  const preprodInstances = instances.filter((i) => i.environment === 'preprod');
  const prodPlayers   = prodInstances.reduce((acc, i) => acc + (i.players ?? 0), 0);
  const preprodPlayers = preprodInstances.reduce((acc, i) => acc + (i.players ?? 0), 0);

  const containers = MOCK_CONTAINERS.filter((c) => c.projectId === project.id);
  const prodDegraded    = prodInstances.some((i) => i.health !== 'healthy');
  const preprodDegraded = preprodInstances.some((i) => i.health !== 'healthy');

  return (
    <div className={styles.pipeline}>
      {/* Colonne Dev + Bêta (aucune flèche de sortie) */}
      <div className={styles.column}>
        <div className={styles.colLabel}>
          <Icon name="sparkles" size={11} />
          Environnements de travail
        </div>
        <div className={styles.colGrid}>
          <article className={styles.card} onClick={() => navigate('/deploiement/environments/dev')} role="button" tabIndex={0}>
            <header className={styles.cardHeader}>
              <div className={styles.envInfo}>
                <EnvBadge env="dev" />
                <h3>Dev</h3>
                <span className={styles.desc}>Envs perso par dev</span>
              </div>
              <Icon name="chevron-right" size={18} />
            </header>
            <div className={styles.stats}>
              <Stat label="Envs actifs" value={devEnvs.length.toString()} tone="primary" />
              <Stat label="Running" value={devEnvs.filter((e) => e.status === 'running').length.toString()} tone="ok" />
            </div>
            <div className={styles.reposPreview}>
              {devEnvs.slice(0, 3).map((e) => {
                const c = containers.find((c) => c.code === e.modelCode);
                return (
                  <Badge key={e.id} tone="primary">
                    <Icon name="user" size={10} /> {e.ownerName.split(' ')[0].toLowerCase()} · {c?.displayName}
                  </Badge>
                );
              })}
              {devEnvs.length > 3 && <Badge tone="muted">+{devEnvs.length - 3}</Badge>}
              {devEnvs.length === 0 && <Badge tone="muted">Aucun env</Badge>}
            </div>
          </article>

          <article className={styles.card} onClick={() => navigate('/deploiement/environments/beta')} role="button" tabIndex={0}>
            <header className={styles.cardHeader}>
              <div className={styles.envInfo}>
                <EnvBadge env="beta" />
                <h3>Bêta</h3>
                <span className={styles.desc}>5 slots fixes · A → E</span>
              </div>
              <Icon name="chevron-right" size={18} />
            </header>
            <div className={styles.stats}>
              <Stat label="Occupés" value={`${betaOccupied} / 5`} tone={betaOccupied === 5 ? 'warning' : 'info'} />
              <Stat label="Libres" value={(5 - betaOccupied).toString()} tone={betaOccupied < 5 ? 'ok' : 'muted'} />
            </div>
            <div className={styles.reposPreview}>
              {betaSlots.map((s) => (
                <Badge key={s.id} tone={s.allocated ? 'warning' : 'muted'}>
                  {s.id} {s.allocated ? '· occupé' : ''}
                </Badge>
              ))}
            </div>
          </article>
        </div>
      </div>

      {/* Séparateur visuel */}
      <div className={styles.divider} aria-hidden>
        <span className={styles.dividerLine} />
      </div>

      {/* Pipeline stable: Pré-prod / Prod */}
      <div className={styles.column}>
        <div className={styles.colLabel}>
          <Icon name="lock" size={11} />
          Pipeline stable
        </div>
        <div className={styles.colStable}>
          <article className={styles.card} onClick={() => navigate('/deploiement/environments/preprod')} role="button" tabIndex={0}>
            <header className={styles.cardHeader}>
              <div className={styles.envInfo}>
                <EnvBadge env="preprod" />
                <h3>Pré-prod</h3>
                <span className={styles.desc}>Copie exacte de la Prod</span>
              </div>
              <Icon name="chevron-right" size={18} />
            </header>
            <div className={styles.stats}>
              <Stat label="Instances" value={preprodInstances.length.toString()} tone={preprodDegraded ? 'warning' : 'ok'} />
              <Stat label="Joueurs" value={preprodPlayers.toString()} />
            </div>
            <div className={styles.reposPreview}>
              <Badge tone="info" dot>Build {containers[0]?.deployedVersions.preprod ?? '—'}</Badge>
            </div>
          </article>

          <article className={styles.card} onClick={() => navigate('/deploiement/environments/prod')} role="button" tabIndex={0}>
            <header className={styles.cardHeader}>
              <div className={styles.envInfo}>
                <EnvBadge env="prod" />
                <h3>Prod</h3>
                <span className={styles.desc}>Utilisateurs finaux</span>
              </div>
              <Icon name="chevron-right" size={18} />
            </header>
            <div className={styles.stats}>
              <Stat label="Instances" value={prodInstances.length.toString()} tone={prodDegraded ? 'warning' : 'ok'} />
              <Stat label="Joueurs" value={prodPlayers.toLocaleString('fr-FR')} tone="primary" />
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
  tone?: 'ok' | 'warning' | 'info' | 'primary' | 'muted';
}
function Stat({ label, value, tone = 'ok' }: StatProps) {
  return (
    <div className={[styles.stat, styles[`tone_${tone}`]].join(' ')}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
    </div>
  );
}
